import { Download, PhoneCall, Search } from "lucide-react";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, DescriptionItem } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { CopyButton, Separator, Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import {
  CallPager, CallRangeLabel, DirectionIcon, MediaCell, MediaHead, SummaryTile,
} from "@/features/call-report";
import { CallAudio } from "@/features/call-audio";
import { CallMarkBadge } from "@/features/call-mark";
import { CallQualityCell, CallQualityDetail } from "@/features/call-quality";
import { CallTypeChip, knownCallType } from "@/features/call-type";
import { LostCallsView } from "@/features/lost-calls";
import { ApiError, fetchFile, qs, saveBlob } from "@/lib/api";
import {
  ABANDONED_DESCRIPTION, abandonedTile, callMark, callOutcome, endedBy, LEG_RELATION_LABELS,
  queueOutcomeChips, queueOutcomeFilter, talkShown, type QueueChipOutcome,
} from "@/lib/call-outcome";
import { DEFAULT_RANGE, RANGES, formatSpan, rangeSince, type Range } from "@/lib/calls";
import {
  CALLS_PAGE_SIZE, useCall, useCallSummary, useCalls, type CallFilters,
} from "@/lib/queries";
import {
  CALL_LEGS, CALL_LEG_DESCRIPTIONS, CALL_LEG_LABELS, broughtInBy, hangupLabel, hangupTone,
  type Cdr, type CdrDetail, type CdrOtherLeg, type CdrSummary,
} from "@/lib/types";
import { formatDateTime, formatDuration, formatPhoneNumber } from "@/lib/utils";
import { useDebounced } from "@/lib/use-debounced";

/**
 * The call report — every leg the switch has finished, filtered, totalled and exportable.
 *
 * Three things about the shape of this screen are deliberate:
 *
 * 1. **The tiles describe the filter, not the page.** They come from `/cdrs/summary`, which runs
 *    the same `WHERE` with no `LIMIT`. Totalling the fifty rows on screen would produce a report
 *    about the reader's scroll position, which is the standard way a call report lies.
 * 2. **A row is a leg, not a call.** The switch writes one CDR per channel, so a rep talking to
 *    a lead is two rows that share no id. The header says so rather than the console guessing a
 *    pairing from timestamps and getting it wrong on transfers and conferences.
 * 3. **The outcome chips are filters.** "21 legs hit NO_ROUTE_DESTINATION" is the finding; being
 *    able to click it and see which twenty-one is what makes it actionable.
 *
 * "Never reached us" (`?view=lost`, platform ADR-178) is the other view: the inbound calls the
 * carrier sent that the switch never took, from the carrier's own log. They are none of the legs
 * here, since a switch that did not take a call wrote no leg for it. The ops alert links to it.
 */

export function CallsPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const view: CallsView = params.get("view") === "lost" ? "lost" : "legs";
  const setView = (next: CallsView) =>
    setParams(next === "lost" ? { view: "lost" } : {}, { replace: true });

  const [search, setSearch] = React.useState("");
  const [range, setRange] = React.useState<Range>(DEFAULT_RANGE);
  const [direction, setDirection] = React.useState("all");
  const [leg, setLeg] = React.useState("all");
  const [answered, setAnswered] = React.useState("all");
  const [cause, setCause] = React.useState<string | null>(null);
  const [queueOutcome, setQueueOutcome] = React.useState<QueueChipOutcome | null>(null);
  const [page, setPage] = React.useState(0);
  const [openId, setOpenId] = React.useState<number | undefined>();
  const [exporting, setExporting] = React.useState(false);
  const debounced = useDebounced(search, 250);

  // Pinned when the filters change rather than read per render: recomputing "24 hours ago" on
  // every render makes a new query key every time, and the table refetches forever.
  const since = React.useMemo(() => rangeSince(range), [range]);

  const filters: CallFilters = React.useMemo(
    () => ({
      q: debounced || undefined,
      since,
      direction: direction === "all" ? undefined : direction,
      leg: leg === "all" ? undefined : leg,
      answered: answered === "all" ? undefined : answered === "yes",
      hangup_cause: cause ?? undefined,
      // Exactly the legs the chip counted (see `queueOutcomeFilter`).
      ...queueOutcomeFilter(queueOutcome),
    }),
    [debounced, since, direction, leg, answered, cause, queueOutcome],
  );

  // Any filter change puts the reader back on the first page. Without this, narrowing a filter
  // while on page 4 shows an empty table that looks like "no calls" rather than "no page 4".
  React.useEffect(() => setPage(0), [filters]);

  // The legs are not read while the other view is open.
  const list = useCalls(filters, page, { enabled: view === "legs" });
  const summary = useCallSummary(filters, { enabled: view === "legs" });

  const filtered =
    Boolean(debounced) || direction !== "all" || leg !== "all" || answered !== "all" ||
    cause !== null || queueOutcome !== null || range !== DEFAULT_RANGE;

  function clearAll() {
    setSearch("");
    setRange(DEFAULT_RANGE);
    setDirection("all");
    setLeg("all");
    setAnswered("all");
    setCause(null);
    setQueueOutcome(null);
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const { blob, headers } = await fetchFile(`/cdrs/export.csv${qs(filters)}`);
      const stamp = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `calls-${stamp}.csv`);
      // The server caps an export; a truncated CSV looks complete once it is open in a
      // spreadsheet, so the only place this can be said is here, now.
      if (headers.get("X-Export-Truncated") === "true") {
        toast.info(
          "Export truncated",
          "The file hit the row cap. Narrow the date range to export the rest.",
        );
      } else {
        toast.success("Export downloaded");
      }
    } catch (error) {
      toast.error(
        "Could not export",
        error instanceof ApiError ? error.message : "The download did not start.",
      );
    } finally {
      setExporting(false);
    }
  }

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? null;
  const from = page * CALLS_PAGE_SIZE;

  if (view === "lost") {
    return (
      <Page>
        <PageHeader
          title="Calls"
          description="Inbound calls the carrier sent us that never reached the switch, or that it cut off within two seconds."
        />
        <CallsViewTabs view={view} onChange={setView} />
        <LostCallsView />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Calls"
        description="Every leg the switch has finished. One row is one leg — a rep talking to a caller is two."
        action={
          <Button variant="outline" onClick={() => void exportCsv()} disabled={exporting}>
            {exporting ? <Spinner /> : <Download />}
            Export CSV
          </Button>
        }
      />

      <CallsViewTabs view={view} onChange={setView} />

      <SummaryTiles summary={summary.data} loading={summary.isPending} />

      <div className="mb-4 space-y-2 empty:hidden">
        <QueueOutcomeChips
          summary={summary.data}
          selected={queueOutcome}
          onSelect={(next) => setQueueOutcome(next === queueOutcome ? null : next)}
        />
        <CauseChips
          summary={summary.data}
          selected={cause}
          onSelect={(next) => setCause(next === cause ? null : next)}
        />
      </div>

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number, name or uuid"
            leading={<Search />}
            className="w-64"
            aria-label="Search calls"
          />
          <Select value={range} onValueChange={(v) => setRange(v as Range)}>
            <SelectTrigger className="w-40" aria-label="Time range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(RANGES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="w-36" aria-label="Filter by direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any direction</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
            </SelectContent>
          </Select>
          <Select value={answered} onValueChange={setAnswered}>
            <SelectTrigger className="w-36" aria-label="Filter by answered">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Answered or not</SelectItem>
              <SelectItem value="yes">Answered</SelectItem>
              <SelectItem value="no">Unanswered</SelectItem>
            </SelectContent>
          </Select>
          <Select value={leg} onValueChange={setLeg}>
            <SelectTrigger className="w-40" aria-label="Filter by leg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any leg</SelectItem>
              {CALL_LEGS.map((l) => (
                <SelectItem key={l} value={l}>
                  {CALL_LEG_LABELS[l]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              Clear
            </Button>
          )}
          <span className="ml-auto flex items-center gap-2 text-xs text-text-muted">
            {/* `isFetching` while data is on screen means a background refresh, not a load —
                showing a skeleton for it would blank a table somebody is reading. */}
            {list.isFetching && !list.isPending && <Spinner className="size-3" />}
            <CallRangeLabel from={from} count={rows.length} total={total} />
          </span>
        </Toolbar>

        {list.isPending ? (
          <TableSkeleton cols={8} rows={8} />
        ) : list.error ? (
          <ErrorState
            error={list.error}
            title="Could not load calls"
            action={
              <Button variant="outline" onClick={() => void list.refetch()}>
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={PhoneCall}
            title={filtered ? "No calls match those filters" : "No calls yet"}
            description={
              filtered
                ? "Try a wider time range, or clear the filters."
                : "Legs appear here as soon as the switch finishes a call."
            }
            action={
              filtered ? (
                <Button variant="outline" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Talk</TableHead>
                  {/* Narrow and centred: it is scanned down the column, not read across a row. */}
                  <TableHead className="w-[5.5rem] text-center">Audio</TableHead>
                  <TableHead>Outcome</TableHead>
                  <MediaHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((call) => (
                  <CallRow key={call.id} call={call} onOpen={() => setOpenId(call.id)} />
                ))}
              </TableBody>
            </Table>
            <CallPager
              page={page}
              count={rows.length}
              total={total}
              onChange={setPage}
              busy={list.isFetching}
            />
          </>
        )}
      </Card>

      <CallDetailDialog
        id={openId}
        onClose={() => setOpenId(undefined)}
        onOpenLeg={setOpenId}
      />
    </Page>
  );
}

type CallsView = "legs" | "lost";

/** The page's two views: every leg the switch finished, and the calls it never took. */
function CallsViewTabs({ view, onChange }: { view: CallsView; onChange: (next: CallsView) => void }) {
  return (
    <Tabs value={view} onValueChange={(next) => onChange(next as CallsView)} className="mb-4">
      <TabsList>
        <TabsTrigger value="legs">All legs</TabsTrigger>
        <TabsTrigger value="lost">Never reached us</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

function CallRow({ call, onOpen }: { call: Cdr; onOpen: () => void }) {
  const outcome = callOutcome(call);
  // An inbound call and a dialer call as every screen marks them (ACL-508); a leg that never met
  // the ACD has no kind, and no chip.
  const type = knownCallType(call.kind);
  return (
    <TableRow
      interactive
      tabIndex={0}
      role="button"
      aria-label={`Call from ${call.caller_number ?? "unknown"} at ${formatDateTime(call.start_time)}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="whitespace-nowrap text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <DirectionIcon direction={call.direction} />
          {formatDateTime(call.start_time)}
        </span>
      </TableCell>
      <Party
        number={call.caller_number}
        name={call.lead_side === "caller" ? call.lead_name : call.caller_name}
      />
      <Party
        number={call.callee_number}
        name={call.lead_side === "callee" ? call.lead_name : call.callee_name}
      />
      <TableCell className="max-w-52 text-text-muted">
        <span className="flex min-w-0 items-center gap-1.5">
          {type && <CallTypeChip type={type} size="sm" short />}
          <span className="truncate">{call.campaign_name ?? (call.campaign_id ? "Deleted campaign" : "—")}</span>
        </span>
      </TableCell>
      <TableCell className="text-right font-mono text-xs">
        {/* Talk time, not duration: a leg that rang for 40 seconds and was never picked up did
            not occupy anybody, and showing 0:40 in a "Talk" column overstates the floor's day.
            Nor did a caller who gave up in a queue: the switch bills their wait. */}
        {talkShown(call) ? formatDuration(call.billsec_seconds) : <span className="text-text-subtle">—</span>}
      </TableCell>
      {/* Stop the row's own click reaching the dialog from inside the hover card: reading the
          figures is not asking to open the leg. */}
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <CallQualityCell call={call} />
      </TableCell>
      <TableCell>
        {/* A redial is a call of its own here, made at once after one nobody answered: the mark
            is what makes three rows seconds apart read as a run and not as a fault. */}
        <span className="inline-flex items-center gap-1.5">
          <Badge tone={outcome.tone} size="sm">
            {outcome.label}
          </Badge>
          <CallMarkBadge call={call} />
        </span>
      </TableCell>
      <MediaCell call={call} />
    </TableRow>
  );
}

/** Numbers get the readable grouping; extensions and names are left exactly as stored. */
/**
 * One end of a leg: the number, and the name over it when we hold one.
 *
 * The name comes from our own contact record where the lead is this party (`lead_side`), and
 * from the switch otherwise. Our own is worth preferring: the trunk sends no caller-id name at
 * all, so on an inbound call the switch has nothing to offer and the only person who can name
 * the caller is the firm.
 */
function Party({ number, name }: { number: string | null; name: string | null }) {
  if (!number) return <TableCell className="text-text-subtle">—</TableCell>;
  return (
    <MonoCell className="whitespace-nowrap">
      {name ? (
        <span className="flex flex-col leading-tight">
          <span className="font-sans text-text">{name}</span>
          <span className="text-2xs text-text-muted">{formatPhoneNumber(number)}</span>
        </span>
      ) : (
        <span className="text-text">{formatPhoneNumber(number)}</span>
      )}
    </MonoCell>
  );
}

// --- the totals ---------------------------------------------------------------------------------

function SummaryTiles({ summary, loading }: { summary?: CdrSummary; loading: boolean }) {
  const abandoned = summary ? abandonedTile(summary) : undefined;
  const tiles: { label: string; value: React.ReactNode; hint?: string }[] = [
    {
      label: "Legs",
      value: summary ? summary.calls.toLocaleString() : "—",
      hint: summary ? `${summary.inbound} in · ${summary.outbound} out` : undefined,
    },
    {
      label: "Answered",
      // A rate of null is "nothing was dialled", which is not 0%.
      value:
        summary?.answer_rate === null || summary === undefined
          ? "—"
          : `${Math.round(summary.answer_rate * 100)}%`,
      hint: summary ? `${summary.answered.toLocaleString()} of ${summary.calls.toLocaleString()}` : undefined,
    },
    {
      label: "Talk time",
      value: summary ? formatSpan(summary.talk_seconds) : "—",
      hint: summary?.avg_talk_seconds != null ? `${formatSpan(summary.avg_talk_seconds)} average` : undefined,
    },
    {
      // Every leg that never reached a rep, whoever ended it: the caller or the line's clock in a
      // queue, or the dialer's safe harbour. The switch answered them all, so `Answered` counts them.
      label: "Abandoned",
      value: abandoned ? abandoned.value.toLocaleString() : "—",
      hint: abandoned?.hint ?? ABANDONED_DESCRIPTION,
    },
  ];

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles.map((tile) => (
        <SummaryTile key={tile.label} {...tile} loading={loading} />
      ))}
    </div>
  );
}

/**
 * The outcome breakdown, as filters.
 *
 * This is where a switch problem shows itself: a healthy campaign is mostly `Completed` and
 * `No answer`, and a pile of `No route` or `Rep not signed in` is a configuration fault nobody
 * would find by scrolling the table. Clicking one narrows the report to it, which is the
 * difference between a number and a lead to follow.
 */
function CauseChips({
  summary, selected, onSelect,
}: {
  summary?: CdrSummary;
  selected: string | null;
  onSelect: (cause: string) => void;
}) {
  const causes = Object.entries(summary?.by_hangup_cause ?? {});
  if (causes.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs text-text-muted">Hangup causes</span>
      {causes.map(([cause, count]) => (
        <FilterChip
          key={cause}
          active={selected === cause}
          count={count}
          onClick={() => onSelect(cause)}
        >
          <span className={hangupTone(cause) === "danger" ? "text-danger-11" : undefined}>
            {hangupLabel(cause)}
          </span>
        </FilterChip>
      ))}
    </div>
  );
}

/**
 * How callers' queue stays ended, as filters: the legs the switch reports as ordinary answered calls
 * (NORMAL_CLEARING, billed seconds) although nobody talked to them. Clicking one narrows the report to
 * exactly the legs it counts, so the rows under it always number what the chip said.
 */
function QueueOutcomeChips({
  summary, selected, onSelect,
}: {
  summary?: CdrSummary;
  selected: QueueChipOutcome | null;
  onSelect: (outcome: QueueChipOutcome) => void;
}) {
  const chips = queueOutcomeChips(summary, selected);
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs text-text-muted">Queue outcomes</span>
      {chips.map((chip) => (
        <FilterChip
          key={chip.outcome}
          active={selected === chip.outcome}
          count={chip.count}
          onClick={() => onSelect(chip.outcome)}
        >
          {chip.label}
        </FilterChip>
      ))}
    </div>
  );
}

function FilterChip({
  active, count, onClick, children,
}: {
  active: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs transition-colors " +
        (active
          ? "border-accent-8 bg-accent-3 text-accent-11"
          : "border-border bg-surface text-text-muted hover:bg-surface-hover hover:text-text")
      }
    >
      {children}
      <span className="tabular-nums text-text-subtle">{count.toLocaleString()}</span>
    </button>
  );
}

// --- one leg in full ------------------------------------------------------------------------------

/**
 * The name to print against one end of a leg: our own contact record where the lead is that
 * party, and the switch's caller id otherwise.
 *
 * Ours first, because the switch has nothing to offer on an inbound call — the trunk sends no
 * caller-id name — and a firm that has held a contact for months should not read them as a
 * number on their own call record.
 */
function partyName(call: CdrDetail, side: "caller" | "callee"): string | null {
  if (call.lead_side === side && call.lead_name) return call.lead_name;
  return (side === "caller" ? call.caller_name : call.callee_name) || null;
}

/**
 * The other rows that are this same conversation, each one a link to itself.
 *
 * The uuid is what a support reader carries between here and the switch's own logs, so it is
 * what the link reads as; the numbers and the endpoint beside it are what tell one leg from
 * another before it is opened, and the relation says which fact put it in this list at all.
 */
function OtherLegs({ legs, onOpen }: { legs: CdrOtherLeg[]; onOpen: (id: number) => void }) {
  if (legs.length === 0) {
    return <span className="text-text-subtle">Nothing relates another leg to this one</span>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {legs.map((leg) => (
        <li key={leg.id} className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <button
            type="button"
            onClick={() => onOpen(leg.id)}
            className="rounded font-mono text-xs text-accent-11 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-8"
          >
            {leg.channel_uuid}
          </button>
          <CopyButton value={leg.channel_uuid} />
          <span className="text-xs text-text-muted">
            {CALL_LEG_LABELS[leg.leg as keyof typeof CALL_LEG_LABELS] ?? leg.leg ?? "Unknown leg"}
            {leg.endpoint_user ? ` · ${leg.endpoint_user}` : ""}
            {" · "}
            {formatPhoneNumber(leg.caller_number ?? "?")} → {formatPhoneNumber(leg.callee_number ?? "?")}
            {" · "}
            {formatDuration(leg.billsec_seconds)} talk
          </span>
          <span className="text-2xs text-text-subtle">{LEG_RELATION_LABELS[leg.relation]}</span>
        </li>
      ))}
    </ul>
  );
}


export function CallDetailDialog({
  id, onClose, onOpenLeg,
}: {
  id: number | undefined;
  onClose: () => void;
  /** Open another leg of the same call, in this same dialog. */
  onOpenLeg: (legId: number) => void;
}) {
  const { data, isPending, error } = useCall(id);

  return (
    <Dialog open={id !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg" variant="drawer">
        <DialogHeader>
          <DialogTitle>Call leg</DialogTitle>
          <DialogDescription>
            One channel as the switch recorded it. The other party's leg is a separate row.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isPending ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <ErrorState error={error} title="Could not load this call" />
          ) : data ? (
            <>
              <dl className="divide-y divide-border">
                <DescriptionItem label="Started">{formatDateTime(data.start_time)}</DescriptionItem>
                <DescriptionItem label="Answered">
                  {data.answer_time ? formatDateTime(data.answer_time) : "Never answered"}
                </DescriptionItem>
                <DescriptionItem label="Ended">{formatDateTime(data.end_time)}</DescriptionItem>
                <DescriptionItem label="Ringing and talking">
                  {formatDuration(data.duration_seconds)} total ·{" "}
                  {talkShown(data) ? `${formatDuration(data.billsec_seconds)} talk` : "no talk"}
                </DescriptionItem>
                <DescriptionItem label="From">
                  {formatPhoneNumber(data.caller_number ?? "—")}
                  {partyName(data, "caller") ? ` · ${partyName(data, "caller")}` : ""}
                </DescriptionItem>
                <DescriptionItem label="To">
                  {formatPhoneNumber(data.callee_number ?? "—")}
                  {partyName(data, "callee") ? ` · ${partyName(data, "callee")}` : ""}
                </DescriptionItem>
                <DescriptionItem label="Direction">
                  <span className="inline-flex items-center gap-2">
                    {data.direction ?? "—"}
                    {knownCallType(data.kind) && <CallTypeChip type={knownCallType(data.kind)!} size="sm" />}
                  </span>
                </DescriptionItem>
                <DescriptionItem label="Leg">
                  <span className="inline-flex items-center gap-2">
                    {CALL_LEG_LABELS[data.leg as keyof typeof CALL_LEG_LABELS] ?? data.leg ?? "—"}
                    {data.leg && CALL_LEG_DESCRIPTIONS[data.leg as keyof typeof CALL_LEG_DESCRIPTIONS] && (
                      <span className="text-xs text-text-muted">
                        {CALL_LEG_DESCRIPTIONS[data.leg as keyof typeof CALL_LEG_DESCRIPTIONS]}
                      </span>
                    )}
                  </span>
                </DescriptionItem>
                <DescriptionItem label="Outcome">
                  <Badge tone={callOutcome(data).tone} size="sm">
                    {callOutcome(data).label}
                  </Badge>
                  {/* The switch's own cause stays beside a queue's wording: it is what an operator
                      searches the switch for, and it reads NORMAL_CLEARING either way. */}
                  {data.hangup_cause && (
                    <span className="ml-2 font-mono text-xs text-text-subtle">
                      {data.hangup_cause}
                    </span>
                  )}
                  {data.queue_name && (
                    <span className="ml-2 text-xs text-text-muted">Queue: {data.queue_name}</span>
                  )}
                </DescriptionItem>
                {/* Which side put the phone down. The hangup cause does not answer it —
                    NORMAL_CLEARING is what both legs of every ordinary call say — and it is the
                    first question asked about a call that ended sooner than anyone expected. */}
                <DescriptionItem label="Ended by">
                  {endedBy(data) ? (
                    <span className="inline-flex items-baseline gap-2">
                      {endedBy(data)!.label}
                      {endedBy(data)!.hint && (
                        <span className="text-xs text-text-muted">{endedBy(data)!.hint}</span>
                      )}
                      <span className="font-mono text-2xs text-text-subtle">
                        {data.hangup_disposition}
                      </span>
                    </span>
                  ) : (
                    "Not recorded by the switch"
                  )}
                </DescriptionItem>
                {/* A recording is of ONE leg, so it belongs in this block rather than the
                    campaign block below: the other party's side of the same conversation is a
                    different row here, and may carry a recording of its own. The transcript
                    goes with the audio it is of, and clicking a line seeks it. */}
                <DescriptionItem label="Recording">
                  <CallAudio callId={data.channel_uuid} hasTranscript={data.has_transcript} />
                </DescriptionItem>
              </dl>

              <Separator className="my-3" />

              <dl className="divide-y divide-border">
                <DescriptionItem label="Campaign">
                  {data.campaign_name ?? (data.campaign_id ? "Deleted campaign" : "—")}
                </DescriptionItem>
                <DescriptionItem label="DID">{data.did ?? "—"}</DescriptionItem>
                <DescriptionItem label="Domain">{data.domain ?? "—"}</DescriptionItem>
                <DescriptionItem label="Contact">
                  {data.lead_id ? (
                    // The name is the link: a reader who wants the person clicks it, and the id
                    // beside it stays copyable for anyone matching this call to the switch's logs.
                    <span className="inline-flex items-baseline gap-2">
                      <Link
                        to={`/contacts/${data.lead_id}`}
                        className="text-accent-11 hover:underline"
                      >
                        {data.lead_name ?? "Deleted lead"}
                      </Link>
                      <span className="font-mono text-2xs text-text-subtle">{data.lead_id}</span>
                      <CopyButton value={data.lead_id} />
                    </span>
                  ) : (
                    "—"
                  )}
                </DescriptionItem>
                <DescriptionItem label="Attempt">
                  {data.attempt_n === null ? "—" : `#${data.attempt_n}`}
                  {data.cadence ? ` · ${data.cadence}` : ""}
                  {data.dial_mode ? ` · ${data.dial_mode}` : ""}
                  {callMark(data) && (
                    <span className="ml-2 inline-flex align-middle">
                      <CallMarkBadge call={data} />
                    </span>
                  )}
                </DescriptionItem>
                <DescriptionItem label="Machine detection">{data.amd_result ?? "—"}</DescriptionItem>
                <DescriptionItem label="Holds and transfers">
                  {data.hold_count} hold{data.hold_count === 1 ? "" : "s"}
                  {data.hold_seconds > 0 ? ` (${formatDuration(data.hold_seconds)})` : ""} ·{" "}
                  {data.transfer_count} transfer{data.transfer_count === 1 ? "" : "s"}
                </DescriptionItem>
                {/* The room is what relates this leg to the others of the same call, which are
                    separate rows here; a leg a colleague was brought in on says by whom and how,
                    so a three-way reads in order. */}
                <DescriptionItem label="Room">
                  {data.room ? (
                    <span className="inline-flex items-center gap-1 font-mono text-xs">
                      {data.room}
                      <CopyButton value={data.room} />
                    </span>
                  ) : (
                    "—"
                  )}
                  {broughtInBy(data) && (
                    <span className="ml-2 text-xs text-text-muted">{broughtInBy(data)}</span>
                  )}
                </DescriptionItem>
                {/* The rows that are the same conversation. A CDR is one channel, so the other
                    party is always a separate row, and finding it by hand meant reading a uuid
                    out of the raw event and searching for it. */}
                <DescriptionItem label="Other legs">
                  <OtherLegs legs={data.other_legs} onOpen={onOpenLeg} />
                </DescriptionItem>
              </dl>

              <Separator className="my-3" />

              <dl className="divide-y divide-border">
                <DescriptionItem label="Audio">
                  {/* The same score and figures the table's hover shows, so the two cannot come
                      to disagree about what a leg sounded like — laid into this row rather than
                      dropped in as the hover's card: there is room here, this is the screen
                      somebody opened to find out, and a bordered card among plain facts reads
                      as a widget from another screen. */}
                  {data.audio_in_mos === null ? (
                    <span className="text-text-subtle">
                      No RTP recorded — nothing was measured on this leg
                    </span>
                  ) : (
                    <CallQualityDetail call={data} />
                  )}
                  {data.write_codec && data.write_codec !== data.read_codec && (
                    <p className="mt-1.5 font-mono text-2xs text-text-subtle">
                      sent as {data.write_codec}
                    </p>
                  )}
                </DescriptionItem>
                <DescriptionItem label="Last application">
                  {data.last_application ?? "—"}
                  {data.last_application_args ? ` ${data.last_application_args}` : ""}
                </DescriptionItem>
                <DescriptionItem label="Channel">
                  <span className="inline-flex items-center gap-1 font-mono text-xs">
                    {data.channel_uuid}
                    <CopyButton value={data.channel_uuid} />
                  </span>
                </DescriptionItem>
              </dl>

              {data.raw_payload && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-text-muted hover:text-text">
                    Raw switch event
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded border border-border bg-surface-sunken p-2 font-mono text-2xs text-text-muted scrollbar-subtle">
                    {JSON.stringify(data.raw_payload, null, 2)}
                  </pre>
                </details>
              )}

              {data.leg === "other" && (
                <Callout tone="info" className="mt-4">
                  This leg carries no campaign context — the dialer never stamped it. Today that
                  is every call a rep places by hand.
                </Callout>
              )}
            </>
          ) : null}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
